import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, X, PaperPlaneTilt, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

let _msgSeq = 0;
const _mid = () => `m-${Date.now()}-${_msgSeq++}`;

export default function OnboardingCopilot({ open, onClose }) {
  const { refresh } = useAuth();
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { apply, mid }
  const sessionRef = useRef(`onboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/onboarding").then((r) => setState(r.data)).catch(() => {});
    if (messages.length === 0) sendKick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, busy]);

  const sendKick = async () => {
    setBusy(true);
    try {
      const r = await api.post("/onboarding/message", { session_id: sessionRef.current, message: "" });
      const mid = _mid();
      setMessages([{ id: mid, role: "assistant", text: r.data.reply, apply: r.data.apply }]);
      if (r.data.state) setState(r.data.state);
      if (r.data.apply) setPending({ apply: r.data.apply, mid });
    } catch {
      setMessages([{ id: _mid(), role: "assistant", text: "Hi! I couldn't reach the assistant just now. You can still configure everything from Settings." }]);
    }
    setBusy(false);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    const nextMsgs = [...messages, { id: _mid(), role: "user", text }];
    setMessages(nextMsgs);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/onboarding/message", { session_id: sessionRef.current, message: text });
      const mid = _mid();
      setMessages([...nextMsgs, { id: mid, role: "assistant", text: r.data.reply, apply: r.data.apply }]);
      if (r.data.state) setState(r.data.state);
      if (r.data.apply) setPending({ apply: r.data.apply, mid });
    } catch {
      setMessages([...nextMsgs, { id: _mid(), role: "assistant", text: "Hmm, I couldn't send that. Try again in a moment." }]);
    }
    setBusy(false);
  };

  const applyPending = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await api.post("/onboarding/apply", pending.apply);
      if (r.data.state) setState(r.data.state);
      await refresh();
      setMessages((prev) => [...prev, { role: "assistant", text: "Saved. What would you like to set up next?" }]);
      setPending(null);
      toast.success("Setting saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  const quicks = ["My hours are 11am-10pm daily", "Suggest a table plan for me", "Pick a to-go mode for a full-service restaurant", "Personalize my AI greeting"];

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[#121914]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="onboarding-copilot">
      <div className="bg-white w-full max-w-lg rounded-2xl border border-[#E1DFD5] shadow-2xl flex flex-col max-h-[85vh]">
        <div className="p-5 border-b border-[#E1DFD5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1E3A2F] flex items-center justify-center">
            <Sparkle size={20} color="#fff" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold">Setup Copilot</div>
            <div className="text-xs text-[#5C6B61]">Included · we'll get you live in minutes</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1D7D2]" data-testid="onboarding-close"><X size={16} className="mx-auto" /></button>
        </div>
        {state && (
          <div className="px-5 pt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#5C6B61]">Progress</span>
              <span className="font-heading font-extrabold" data-testid="onboarding-progress">{state.done}/{state.total} · {state.percent}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#E6E2D6] overflow-hidden">
              <div className="h-full bg-[#1E3A2F] transition-[width]" style={{ width: `${state.percent}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {state.steps.map((s) => (
                <span key={s.key} className={`text-[10px] px-2 py-0.5 rounded-full border ${s.done ? "bg-[#C2D1C1] border-[#1E3A2F]/20 text-[#1E3A2F]" : "bg-white border-[#E1DFD5] text-[#5C6B61]"}`} data-testid={`onboarding-step-${s.key}`}>
                  {s.done ? "✓ " : "· "}{s.label.replace("your ", "").replace("Set ", "").replace("Personalize ", "")}
                </span>
              ))}
            </div>
          </div>
        )}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.map((m, i) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm leading-snug ${m.role === "user" ? "bg-[#1E3A2F] text-white" : "bg-[#F7F6F2] text-[#121914]"}`} data-testid={`onboarding-msg-${i}`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl px-4 py-2 text-sm bg-[#F7F6F2] text-[#5C6B61] flex items-center gap-2">
                <ArrowClockwise size={14} className="animate-spin" /> Thinking…
              </div>
            </div>
          )}
          {pending && (
            <div className="paper-card p-3 bg-[#C2D1C1]/40 border-[#1E3A2F]/20" data-testid="onboarding-apply-preview">
              <div className="text-xs text-[#5C6B61] mb-1">The copilot wants to save:</div>
              <pre className="text-xs whitespace-pre-wrap break-words text-[#121914]">{JSON.stringify(pending.apply, null, 2)}</pre>
              <div className="flex gap-2 mt-2">
                <button onClick={applyPending} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="onboarding-apply-btn"><CheckCircle size={14} color="#fff" /> Apply this change</button>
                <button onClick={() => setPending(null)} className="btn-ghost !py-1.5 !px-3 text-xs">Not now</button>
              </div>
            </div>
          )}
        </div>
        {messages.length <= 1 && (
          <div className="px-5 pb-1 flex flex-wrap gap-1.5">
            {quicks.map((q) => (
              <button key={q} onClick={() => { setInput(q); setTimeout(send, 0); }} className="tag tag-notified text-[11px]" data-testid={`onboarding-quick-${q.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}>{q}</button>
            ))}
          </div>
        )}
        <form onSubmit={send} className="p-4 border-t border-[#E1DFD5] flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tell the copilot what to set…" className="input-field flex-1" data-testid="onboarding-input" />
          <button disabled={busy || !input.trim()} className="btn-primary !p-2.5" data-testid="onboarding-send"><PaperPlaneTilt size={16} color="#fff" /></button>
        </form>
      </div>
    </div>
  );
}
